import type { GuideId, Guide, GuideCopy } from '../content';

const secretHitlerRules = {
  label: 'Secret Hitler 官方規則',
  url: 'https://www.secrethitler.com/assets/Secret_Hitler_Rules.pdf',
};
const secretHitlerOnline = { label: 'Secret Hitler.io 線上版', url: 'https://secrethitler.io/' };
const werewolfRules = {
  label: 'Bézier Games 出版的 Ultimate Werewolf（終極狼人）',
  url: 'https://beziergames.com/products/ultimate-werewolf',
};
const oneNight = {
  label: 'Bézier Games 出版的 One Night Ultimate Werewolf（一夜終極狼人）',
  url: 'https://beziergames.com/products/one-night-ultimate-werewolf',
};
const browserWerewolf = { label: 'BoardGameJoin 上的狼人殺', url: 'https://boardgamejoin.com/en/games/werewolf/' };
const amongUs = { label: 'Innersloth 開發的 Among Us', url: 'https://www.innersloth.com/games/among-us/' };

export const guides: Record<GuideId, Guide> = {
  social_deduction_games_online: {
    slug: 'social-deduction-games-online',
    breadcrumb: '線上陣營桌遊',
    title: '線上陣營桌遊：阿瓦隆與狼人殺怎麼選？',
    lead: '阿瓦隆、揭密希特勒與狼人殺都有瀏覽器版，適合朋友揪團玩陣營桌遊。三款分別靠任務、法案與夜間行動推理。Among Us（太空狼人殺）需安裝，側重移動線索。',
    preview: true,
    table: {
      caption: '為下一次朋友聚會挑選遊戲',
      columns: ['遊戲與人數', '適合的玩法偏好', '實際遊玩的差異'],
      rows: [
        {
          title: '阿瓦隆 · 5–10 位真人，或單人／與朋友和機器人一起玩',
          cells: [
            '討論、公開投票與秘密破壞任務。',
            '一般真人對局免費，供 5–10 位擁有帳號的玩家參與。你可以單人遊玩，也可以邀請朋友，讓機器人補齊空位；在設定中選擇目前可用的對局人數和模式。任務期間沒有人會被淘汰。',
          ],
        },
        {
          title: 'Secret Hitler · 5–10 人',
          cells: [
            '選出政府，並討論法案牌究竟發生了什麼事。',
            'Secret Hitler.io 提供瀏覽器版，支援帳號與私人對局。處決可能讓玩家退出對局。',
          ],
          source: secretHitlerOnline,
        },
        {
          title: '狼人殺 · BoardGameJoin 上支援 4–30 人',
          cells: [
            '經典日夜循環，圍繞指控與特殊角色展開推理。',
            '這個瀏覽器版本適合 5–10 人的團體。它會處理夜間行動和投票，但需要房主推進階段；玩家可能被淘汰。',
          ],
          source: browserWerewolf,
        },
        {
          title: 'Among Us（太空狼人殺） · 4–15 人',
          cells: [
            '四處移動、完成任務並建立不在場證明。',
            '需要安裝遊戲，是瀏覽器以外的選擇。開局前請確認所有人的遊戲平台和聊天設定。',
          ],
          source: amongUs,
        },
      ],
    },
    sections: [
      {
        title: '用瀏覽器就能玩，還是需要下載？',
        paragraphs: [
          '本站阿瓦隆用瀏覽器就能玩，免下載應用程式。一般真人對局免費，適合 5–10 人；每位朋友需要自己的帳號和裝置，登入後透過房間連結加入。上表也列出了揭密希特勒與狼人殺的瀏覽器版本，Among Us 則需要安裝遊戲。',
          '想先試一局，可以單人玩或邀請朋友，讓機器人補齊空位。AI 對局需要帳號，模式與人數依設定中的目前可用情況而定，部分人數需要 Premium。機器人討論支援英語、俄語、繁體中文、西班牙語和越南語，開局前請先選好討論語言。',
        ],
      },
      {
        title: '阿瓦隆與揭密希特勒：任務結果還是法案線索？',
        paragraphs: [
          '如果你們喜歡反覆檢驗對同一批玩家的信任，可以選擇阿瓦隆。所有人都對提議的隊伍投票，但只有隊員秘密提交任務牌。任務失敗會將懷疑範圍縮小到隊員；任務成功的隊伍中，也可能有選擇不破壞任務的壞人。',
          '如果你們喜歡聽取關於私下傳牌的互相矛盾的說法，可以選擇 Secret Hitler。總統和總理從抽到的牌中選擇一項法案，因此法案結果與兩人的解釋都很重要。法案牌堆帶來的不確定性，是阿瓦隆基本版任務牌所沒有的。',
        ],
        source: secretHitlerRules,
      },
      {
        title: '狼人殺與太空狼人殺：夜間行動還是移動線索？',
        paragraphs: [
          '經典狼人殺和 Mafia（殺人遊戲）的核心是淘汰嫌疑人並解讀夜間行動。遠端遊玩時，可以選擇能處理所用規則的線上版本，或事先約定由誰主持。上面的 BoardGameJoin 版本會自動處理秘密行動，但階段切換仍需房主操作。',
          'Among Us（太空狼人殺）在討論之間加入了行動環節：船員執行任務，冒名者進行破壞和擊殺。會議會將大家目擊的情況轉化為指控。它適合希望在辯論之間也有事可做的朋友；阿瓦隆則讓大家集中討論共同的任務決策。',
        ],
        source: amongUs,
      },
      {
        title: '不想提早出局：阿瓦隆與一夜狼人怎麼選？',
        paragraphs: [
          '阿瓦隆的任務階段讓每位玩家始終參與討論和每次組隊投票。沒被選入任務隊伍只是暫時的：你仍然能投票、觀察結果，並在下次被選中。刺殺是終局決策，不會讓玩家接連被淘汰、只能旁觀剩下的遊戲。',
          '並非所有狼人遊戲都有淘汰機制。One Night Ultimate Werewolf（一夜終極狼人）是一款獨立的 3–10 人遊戲，只有一夜、一次討論，沒有提前淘汰。出版商提供的瀏覽器應用程式是旁白輔助工具，單靠它並不能組成完整的遠端多人遊戲房間。',
        ],
        source: oneNight,
      },
      {
        title: '5–10 人朋友揪團：先約好語言和聊天方式',
        paragraphs: [
          '傳送遊戲連結前，先確認實際參加的人數、選擇共同語言，並決定如何交流。在本站玩阿瓦隆，每位朋友都需要自己的帳號和裝置。先使用熟悉的角色，選用角色和擴充留到之後再嘗試。',
          '如果準備好的人少於五位，你可以單人遊玩，或邀請朋友，讓機器人補齊空位。若想玩一般真人對局，可以另約時間或招募玩家。玩家和揪團佈告欄幫助大家互相聯絡，但不會自動填滿房間，也不保證立刻配對到對局。',
        ],
      },
      {
        title: '朋友還沒到齊，先用機器人練習任務投票',
        paragraphs: [
          '體驗任務流程不必先湊齊一整團人。單人機器人對局能讓你提議隊伍、投票並查看任務結果；有引導的「首次遊戲」可以幫助你入門。你也可以邀請朋友，讓機器人補齊空位，並在設定中選擇對局人數和模式。',
          '用機器人對局理解梅林掌握的資訊和最後刺殺如何與任務銜接，再把這些決策教給真人朋友。大廳的 AI 設定會顯示目前可用的模式和對局人數。',
        ],
      },
    ],
  },
  games_like_secret_hitler: {
    slug: 'games-like-secret-hitler',
    breadcrumb: '類似揭密希特勒的桌遊',
    title: '類似揭密希特勒的桌遊：阿瓦隆有何不同？',
    lead: '喜歡 Secret Hitler（揭密希特勒）的隱藏陣營與投票，可試阿瓦隆這款陣營桌遊。它靠任務結果推理，沒有共用法案牌堆，任務期間也不淘汰玩家。',
    table: {
      caption: 'Secret Hitler 與阿瓦隆：決策方式有哪些變化',
      columns: ['決策環節', 'Secret Hitler', '阿瓦隆'],
      rows: [
        {
          title: '批准的對象',
          cells: ['總統及其提名的總理。', '任務隊伍，人數取決於目前任務和玩家總人數。'],
        },
        {
          title: '秘密行動',
          cells: ['當選的兩人抽取並棄置法案牌。', '每位被選中的玩家秘密提交一張任務牌。'],
        },
        {
          title: '壞結果可能由什麼造成？',
          cells: ['法案選擇，以及抽到的牌。', '基本版中，至少一位邪惡陣營隊員選擇了「失敗」。'],
        },
        {
          title: '參與方式',
          cells: ['處決可能讓玩家在遊戲結束前退出。', '任務期間，每個人始終參與討論和投票。'],
        },
        {
          title: '特殊的終局條件',
          cells: [
            '通過三項法西斯法案後，選希特勒為總理可能直接結束遊戲。',
            '三次任務成功後，邪惡陣營仍可透過認出梅林而獲勝。',
          ],
        },
      ],
    },
    sections: [
      {
        title: '從揭密希特勒換到阿瓦隆，線上怎麼開局？',
        paragraphs: [
          '本站阿瓦隆免下載應用程式。湊齊 5–10 位真人後，每人用自己的帳號和裝置登入；房主在大廳建立房間，再把連結傳給朋友。一般真人對局免費，先用簡單角色配置體驗組隊投票，避免第一次就加入太多額外資訊。',
          '朋友還沒到齊，可以在「與 AI 對戰」中選擇「單人」或「和好友們」，由機器人補齊空位。AI 需要帳號；設定會顯示目前可用的模式、人數與 Premium 要求。討論語言支援英語、俄語、繁體中文、西班牙語和越南語，請先確認設定，AI 對局的使用條件與免費真人局不同。',
        ],
      },
      {
        title: '從選政府到選隊伍：兩種投票有何不同？',
        paragraphs: [
          '兩款遊戲都要求多數人批准提案，平票則否決。留意誰支持過誰，仍然很有用。在阿瓦隆中，要問清楚一個人為什麼支持整支隊伍，不要只把隊長當成唯一需要接受檢視的人。',
          'Secret Hitler 的法案抽牌可能讓忠於自由派的執政者也沒有自由派法案可選。阿瓦隆基本版沒有共用的法案牌堆：好人隊員必須選擇「成功」，壞人隊員則可以選擇「成功」或「失敗」。因此，一張「失敗」牌能透露隊伍的情況，但無法指出究竟是誰出的。',
        ],
        source: secretHitlerRules,
      },
      {
        title: '任務成功為什麼不能證明隊員都是好人？',
        paragraphs: [
          '假設在五人對局中，愛麗絲和班參加第一次任務。三票贊成就足以讓他們出發。基本配置下，如果任務失敗，至少一人屬於邪惡陣營；如果任務成功，兩人中的任何一個仍可能是為了贏得信任而選擇「成功」的壞人。',
          '把這個結果與他們之後的投票和組隊提案進行比較。再次使用成功的隊伍是需要討論的決定，並不代表這些玩家已被證實為好人。七人以上對局的第四次任務需要兩張「失敗」牌才會失敗；成功的結果中也可能出現一張「失敗」。',
        ],
      },
      {
        title: '梅林與刺殺：阿瓦隆多了哪層身分推理？',
        paragraphs: [
          '在 Secret Hitler 中，某位邪惡玩家的身分是選舉獲勝條件的關鍵。阿瓦隆則讓好人梅林在開局時取得有關邪惡陣營的資訊。梅林必須引導好人，同時避免暴露自己掌握的資訊，因為三次任務成功後，邪惡陣營仍有一次刺殺機會。',
          '不要直接沿用「好人應該公開所有資訊」的習慣。自信的解釋可能幫助任務成功，也可能幫助邪惡陣營認出梅林。角色指南會說明梅林能看到什麼，以及哪些選用角色會改變這些資訊。',
        ],
      },
      {
        title: '喜歡揭密希特勒，什麼時候值得換阿瓦隆？',
        paragraphs: [
          '如果你們想體驗不同規模的任務隊伍、從任務結果中推理，並讓所有人持續參與，可以試試阿瓦隆。如果朋友最喜歡法案的不確定性和行政權力，繼續玩 Secret Hitler 也很好。兩款遊戲都有瀏覽器版本，能用瀏覽器遊玩並不是換遊戲的唯一理由。',
          '在本站玩一般真人阿瓦隆時，請湊齊 5–10 人，讓大家登入，並採用簡單的選用角色和擴充配置。你也可以先用機器人學習。不要直接假設提案被否決或刺殺的規則與實體桌遊完全一致，先閱讀下方的平台差異。',
        ],
        source: { label: 'Secret Hitler.io 的規則與瀏覽器版本', url: 'https://secrethitler.io/rules' },
      },
      {
        title: '先用機器人熟悉任務牌，再帶朋友開局',
        paragraphs: [
          '如果玩 Secret Hitler 的朋友還沒上線，你可以先單人和機器人玩阿瓦隆。有引導的「首次遊戲」幫助你學習組隊投票與任務牌，不必先組織一整團人。觀察任務成功如何建立信任，卻不能證明某人一定是好人。',
          '機器人對局也讓你在教朋友之前，先了解梅林和終局刺殺。你也可以邀請朋友，讓機器人補齊空位。在設定中選擇對局人數和模式；這裡會顯示目前可用情況，並標示需要 Premium 的對局人數。',
        ],
      },
    ],
  },
  avalon_vs_werewolf: {
    slug: 'avalon-vs-werewolf',
    breadcrumb: '阿瓦隆與狼人殺比較',
    title: '阿瓦隆和狼人殺的差別：不想提早出局怎麼選？',
    lead: '阿瓦隆是靠組隊投票與任務結果推理的陣營桌遊，任務期間無人被淘汰，每位玩家都能持續討論。經典狼人殺靠日夜行動與放逐投票，適合喜歡夜間技能的玩家。',
    table: {
      caption: '經典狼人殺或 Mafia 與阿瓦隆基本版比較',
      columns: ['遊戲環節', '經典狼人殺／Mafia', '阿瓦隆'],
      rows: [
        {
          title: '集體決策',
          cells: ['投票淘汰一名嫌疑人。', '批准或否決提議的任務隊伍。'],
        },
        {
          title: '秘密行動',
          cells: ['攻擊、查驗等夜間行動，細節取決於所用規則。', '被選中的玩家提交「成功」或「失敗」任務牌。'],
        },
        {
          title: '被懷疑之後',
          cells: ['被淘汰可能代表無法繼續主動參與。', '你仍能參與討論和每一次組隊投票。'],
        },
        {
          title: '不斷累積的線索',
          cells: ['討論、投票、夜間結果與角色資訊。', '組隊提案、公開的贊成或反對票與任務結果。'],
        },
        {
          title: '主持方式',
          cells: ['經典實體遊戲通常需要主持人；線上工具可以處理行動。', '本平台會處理秘密角色、投票和任務結果。'],
        },
      ],
    },
    sections: [
      {
        title: '玩線上狼人殺的朋友，怎麼開阿瓦隆房間？',
        paragraphs: [
          '可以先單人玩阿瓦隆，也可以邀請朋友，讓機器人補齊空位，練習「誰上任務」而不是「誰被放逐」。AI 需要帳號；進入設定確認模式、人數和目前可用情況，部分人數需要 Premium。機器人討論支援英語、俄語、繁體中文、西班牙語和越南語。',
          '真人朋友到齊後，開一般阿瓦隆房間需 5–10 人，每人用自己的帳號和裝置登入。本站在瀏覽器中免費提供一般真人對局，免下載應用程式；房主分享房間連結即可邀請朋友。先約好發言語言和聊天方式，揪團佈告欄能幫助聯絡玩家，但不保證即時配對。',
        ],
      },
      {
        title: '阿瓦隆不提早淘汰，被懷疑後還能做什麼？',
        paragraphs: [
          '在經典狼人殺中，村民白天討論嫌疑人，隱藏的敵人在夜間行動。被淘汰的玩家通常不再參與正式討論和投票。Mafia 有許多地方規則，開局前應先約定版本，不要假設所有角色和勝利條件都一樣。',
          '在阿瓦隆中，沒被選進隊伍並不代表出局。這位玩家仍可質疑提案、投票，並成為下一位隊長。即使是大家都不信任的玩家，也能影響結果，這改變了團體處理懷疑的方式。',
        ],
        source: werewolfRules,
      },
      {
        title: '從放逐投票到組隊投票：五人局怎麼判斷？',
        paragraphs: [
          '不要只停留在「愛麗絲聽起來像壞人」，還要問自己會批准哪支隊伍，為什麼。五人對局的第一次任務需要兩人，五位玩家中至少三位必須贊成。只有選中的兩人選擇任務牌，但所有人都能分析結果。',
          '基本版任務中出現「失敗」，表示至少一位隊員屬於邪惡陣營，但不會揭露是誰破壞了任務。「成功」也不能證明兩人都是好人，因為壞人可以選擇「成功」。應繼續比較每個人提議的隊伍和投票，不要憑一次結果就下定論。',
        ],
      },
      {
        title: '狼人殺角色與阿瓦隆角色：預言家和梅林的差別',
        paragraphs: [
          '經典狼人殺的預言家透過夜間行動取得資訊。阿瓦隆的梅林在開局時取得資訊，但莫德雷德等角色屬於例外。阿瓦隆基本版沒有每夜重複的查驗。梅林的挑戰是幫助好人，同時不被認出。',
          '三次任務失敗後，邪惡陣營獲勝。三次任務成功後，邪惡陣營仍能嘗試刺殺梅林。這是遊戲的最後決策，不是先淘汰一人，再讓其他人繼續玩更多回合。',
        ],
      },
      {
        title: '一夜狼人也不提早淘汰，玩法有何不同？',
        paragraphs: [
          'One Night Ultimate Werewolf（一夜終極狼人）本身就沒有提前淘汰，也不需要主持人。它包含一夜和接下來的討論，而不是經典狼人殺反覆進行的日夜循環。如果你們已經在玩一夜狼人，阿瓦隆帶來的是連續的任務投票和結果；讓所有人都能參與並不是阿瓦隆獨有的特色。',
        ],
        source: oneNight,
      },
      {
        title: '想持續推理，還是喜歡夜間技能？',
        paragraphs: [
          '如果想持續討論、保持固定的 5–10 人團體，並有多次機會檢驗隊伍，可以選擇一般真人阿瓦隆。阿瓦隆的機器人對局也讓你能單人或與朋友體驗同樣的任務決策。如果你們喜歡夜間能力、淘汰嫌疑人和不斷變化的在場人數，可以選擇經典狼人殺或 Mafia。狼人殺也有線上版本，包括這裡連結的瀏覽器版本。',
          '轉換遊戲時，先教組隊投票和任務牌，再解釋梅林與刺殺。請使用我們規則中的人數和角色配置表，不要把 Mafia 的角色平衡配置直接套用到阿瓦隆。',
        ],
        source: browserWerewolf,
      },
      {
        title: '狼人殺玩家先用機器人練習組隊投票',
        paragraphs: [
          '試試「單人」模式或有引導的「首次遊戲」，把熟悉的淘汰投票換成任務隊伍投票。機器人補齊座位，你則自己參與討論並作出遊戲決策。留意即使沒被選進任務隊伍，你仍然可以投票和觀察結果。',
          '透過這局遊戲了解梅林的開局資訊和刺殺決策，再把所學帶給狼人殺或 Mafia 的朋友。你也可以邀請朋友，讓機器人補齊空位。在設定中選擇對局人數、模式和支援的討論語言，這裡會顯示目前可用情況。',
        ],
      },
    ],
  },
};

export const copy: GuideCopy = {
  tocTitle: '本文內容',
  comparisonLabel: '遊戲比較',
  setupLabel: '參與條件與開局',
  aiAccessNote: '需要帳號。語言、模式與 Premium 要求請見設定。',
  home: '首頁',
  breadcrumbLabel: '麵包屑導覽',
  setupTitle: '在這裡玩阿瓦隆：和朋友或機器人一起',
  humanTitle: '和真人朋友進行一般對局',
  playersLabel: '玩家',
  humanPlayers: '5–10 位真人，每人使用自己的裝置。',
  accessLabel: '參與條件',
  humanAccess: '免費的瀏覽器對局。每位玩家都需要帳號。',
  discussionLabel: '討論',
  humanDiscussion: '文字聊天，可選語音聊天（測試版）。',
  findingGroupLabel: '尋找隊友',
  findingGroup: '與朋友約好時間，或使用玩家和揪團佈告欄。',
  boardsNote: '佈告欄幫助大家協調揪團，不保證即時配對。',
  aiTitle: '單人嘗試，或與朋友和機器人一起玩',
  aiIntro:
    '你可以單人遊玩，也可以邀請朋友，讓機器人補齊空位。在設定中選擇對局人數和模式。剛接觸阿瓦隆？試試有引導的「首次遊戲」。',
  aiAvailability:
    '你需要帳號。機器人討論支援英語、俄語、繁體中文、西班牙語和越南語。在設定中選擇模式；這裡會顯示目前可用情況，並標示需要 Premium 的對局人數。',
  startLabel: '開始玩阿瓦隆',
  aiCta: '試試機器人對局',
  lobbyCta: '開啟阿瓦隆大廳',
  findGroupCta: '尋找團體',
  rulesCta: '學習規則',
  sourceLabel: '來源',
  variantTitle: '第一次開局前，請確認這兩條規則',
  proposalVariant:
    '原版阿瓦隆桌遊中，連續五支隊伍被否決，邪惡陣營就會獲勝。在本站，連續四次提案被否決後，第五支隊伍通常無需投票便會執行任務；情節卡可能影響隊伍批准。',
  assassinationVariant:
    '原版遊戲中，邪惡陣營討論後，由刺客最後猜測誰是梅林。在本站的一般真人房間，刺殺由邪惡陣營共同處理。機器人對局也會先召開邪惡陣營議會，再作出刺殺決策。選用角色和自訂角色可能改變可選目標，請確認房間設定。',
  fullRulesCta: '閱讀完整規則與人數配置表',
  previewAlt: '阿瓦隆遊戲面板，顯示八個玩家座位和任務結果',
  previewCaption: '展示任務結果的阿瓦隆面板，取自本站現有的遊戲預覽。',
  nextTitle: '先和機器人入門，或帶朋友一起玩',
  nextAi:
    '想在邀請一整團朋友前先學會玩法？開啟大廳中的「與 AI 對戰」，查看可用模式，並選擇「單人」或有引導的「首次遊戲」。要邀請朋友，請選擇「和好友們」，分享已準備好的房間連結，讓他們在你開始前加入。',
  nextHuman:
    '請 5–10 位朋友建立帳號，約定語言和交流方式，然後開啟大廳並建立房間。把房間連結分享給大家。剛接觸阿瓦隆？先教組隊投票和任務牌，再加入選用角色。',
  arrangeLabel: '遊玩或安排阿瓦隆對局',
  browseGroupsCta: '瀏覽團體',
  findPlayersCta: '尋找玩家',
  merlinCta: '了解梅林',
  relatedLabel: '更多社交推理遊戲指南',
  relatedTitle: '繼續比較',
  ownership: '遊戲名稱歸各自權利人所有。本比較不代表他們認可或推薦本平台。',
};
